/**
 * Référentiel des villes du formulaire public
 * (docs/FORMULAIRE_PUBLIC.md, annexe B).
 *
 * Une seule liste pour toute l'application. Pour les quatre gouvernorats du
 * Grand Tunis — là où se trouvent les points de vente — le choix se fait **par
 * délégation** ; pour les vingt autres, **par gouvernorat**. Dernière option :
 * « Autre ville » et un champ libre.
 *
 * Ce que le formulaire **transmet** est le code stable (`tunis-la-marsa`) ;
 * ce que l'API **écrit** dans `personnes.ville` est le libellé français
 * (« La Marsa »). Raison : `personnes.ville` est du texte libre depuis le
 * premier jour — les CV et les plateformes y déposent ce qu'ils veulent — et le
 * filtre de liste regroupe les fiches par clé de libellé
 * (`filtres-liste.ts` § `cleVille`). Y glisser des codes mélangerait deux
 * vocabulaires dans la même colonne et couperait le regroupement en deux. Le
 * code choisi reste dans `soumissions_formulaire.donnees`, qui garde la saisie
 * telle qu'elle a été faite.
 *
 * Le référentiel vit ici, pas en base : il ne se règle pas, il ne se sème pas,
 * et l'API comme l'écran en ont besoin au même instant.
 */

export interface Ville {
  /** Code stable, jamais traduit : `ariana-la-soukra`, `sfax`. */
  code: string;
  libelle: string;
  libelleAr: string;
  /** Gouvernorat de rattachement, pour grouper la liste déroulante. */
  gouvernorat: string;
  gouvernoratAr: string;
}

/** Code du choix « Autre ville », qui ouvre le champ libre. */
export const VILLE_AUTRE = 'autre';

function delegations(
  gouvernorat: string,
  gouvernoratAr: string,
  prefixe: string,
  entrees: ReadonlyArray<readonly [string, string, string]>,
): Ville[] {
  return entrees.map(([code, libelle, libelleAr]) => ({
    code: `${prefixe}-${code}`,
    libelle,
    libelleAr,
    gouvernorat,
    gouvernoratAr,
  }));
}

const TUNIS = delegations('Tunis', 'تونس', 'tunis', [
  ['bab-el-bhar', 'Bab El Bhar', 'باب البحر'],
  ['bab-souika', 'Bab Souika', 'باب سويقة'],
  ['carthage', 'Carthage', 'قرطاج'],
  ['cite-el-khadra', 'Cité El Khadra', 'حي الخضراء'],
  ['djebel-jelloud', 'Djebel Jelloud', 'جبل الجلود'],
  ['el-hrairia', 'El Hrairia', 'الحرايرية'],
  ['el-kabaria', 'El Kabaria', 'الكبارية'],
  ['el-menzah', 'El Menzah', 'المنزه'],
  ['el-omrane', 'El Omrane', 'العمران'],
  ['el-omrane-superieur', 'El Omrane Supérieur', 'العمران الأعلى'],
  ['el-ouardia', 'El Ouardia', 'الوردية'],
  ['ettahrir', 'Ettahrir', 'التحرير'],
  ['ezzouhour', 'Ezzouhour', 'الزهور'],
  ['la-goulette', 'La Goulette', 'حلق الوادي'],
  ['la-marsa', 'La Marsa', 'المرسى'],
  ['la-medina', 'La Médina', 'المدينة'],
  ['le-bardo', 'Le Bardo', 'باردو'],
  ['le-kram', 'Le Kram', 'الكرم'],
  ['sejoumi', 'Séjoumi', 'السيجومي'],
  ['sidi-el-bechir', 'Sidi El Béchir', 'سيدي البشير'],
  ['sidi-hassine', 'Sidi Hassine', 'سيدي حسين'],
]);

const ARIANA = delegations('Ariana', 'أريانة', 'ariana', [
  ['ariana-ville', 'Ariana Ville', 'أريانة المدينة'],
  ['ettadhamen', 'Ettadhamen', 'التضامن'],
  ['kalaat-el-andalous', 'Kalâat el-Andalous', 'قلعة الأندلس'],
  ['la-soukra', 'La Soukra', 'سكرة'],
  ['mnihla', 'Mnihla', 'المنيهلة'],
  ['raoued', 'Raoued', 'رواد'],
  ['sidi-thabet', 'Sidi Thabet', 'سيدي ثابت'],
]);

const BEN_AROUS = delegations('Ben Arous', 'بن عروس', 'ben-arous', [
  ['ben-arous', 'Ben Arous', 'بن عروس'],
  ['bou-mhel-el-bassatine', 'Bou Mhel el-Bassatine', 'بومهل البساتين'],
  ['el-mourouj', 'El Mourouj', 'المروج'],
  ['ezzahra', 'Ezzahra', 'الزهراء'],
  ['fouchana', 'Fouchana', 'فوشانة'],
  ['hammam-chott', 'Hammam Chott', 'حمام الشط'],
  ['hammam-lif', 'Hammam Lif', 'حمام الأنف'],
  ['megrine', 'Mégrine', 'مقرين'],
  ['mohamedia', 'Mohamedia', 'المحمدية'],
  ['mornag', 'Mornag', 'مرناق'],
  ['nouvelle-medina', 'Nouvelle Médina', 'المدينة الجديدة'],
  ['rades', 'Radès', 'رادس'],
]);

const MANOUBA = delegations('La Manouba', 'منوبة', 'manouba', [
  ['borj-el-amri', 'Borj El Amri', 'برج العامري'],
  ['den-den', 'Den Den', 'الدندان'],
  ['djedeida', 'Djedeida', 'الجديدة'],
  ['douar-hicher', 'Douar Hicher', 'دوار هيشر'],
  ['el-battan', 'El Battan', 'البطان'],
  ['la-manouba', 'La Manouba', 'منوبة'],
  ['mornaguia', 'Mornaguia', 'المرناقية'],
  ['oued-ellil', 'Oued Ellil', 'وادي الليل'],
  ['tebourba', 'Tebourba', 'طبربة'],
]);

/** Les vingt autres gouvernorats : un seul choix chacun, sans délégation. */
const AUTRES: ReadonlyArray<readonly [string, string, string]> = [
  ['beja', 'Béja', 'باجة'],
  ['bizerte', 'Bizerte', 'بنزرت'],
  ['gabes', 'Gabès', 'قابس'],
  ['gafsa', 'Gafsa', 'قفصة'],
  ['jendouba', 'Jendouba', 'جندوبة'],
  ['kairouan', 'Kairouan', 'القيروان'],
  ['kasserine', 'Kasserine', 'القصرين'],
  ['kebili', 'Kébili', 'قبلي'],
  ['le-kef', 'Le Kef', 'الكاف'],
  ['mahdia', 'Mahdia', 'المهدية'],
  ['medenine', 'Médenine', 'مدنين'],
  ['monastir', 'Monastir', 'المنستير'],
  ['nabeul', 'Nabeul', 'نابل'],
  ['sfax', 'Sfax', 'صفاقس'],
  ['sidi-bouzid', 'Sidi Bouzid', 'سيدي بوزيد'],
  ['siliana', 'Siliana', 'سليانة'],
  ['sousse', 'Sousse', 'سوسة'],
  ['tataouine', 'Tataouine', 'تطاوين'],
  ['tozeur', 'Tozeur', 'توزر'],
  ['zaghouan', 'Zaghouan', 'زغوان'],
];

/** Libellé de groupe des gouvernorats choisis sans délégation. */
export const GROUPE_AUTRES_GOUVERNORATS = 'Autres gouvernorats';
export const GROUPE_AUTRES_GOUVERNORATS_AR = 'ولايات أخرى';

export const VILLES: readonly Ville[] = [
  ...TUNIS,
  ...ARIANA,
  ...BEN_AROUS,
  ...MANOUBA,
  ...AUTRES.map(([code, libelle, libelleAr]) => ({
    code,
    libelle,
    libelleAr,
    gouvernorat: GROUPE_AUTRES_GOUVERNORATS,
    gouvernoratAr: GROUPE_AUTRES_GOUVERNORATS_AR,
  })),
];

const PAR_CODE = new Map(VILLES.map((v) => [v.code, v]));

export function villeParCode(code: string | null | undefined): Ville | null {
  return (code && PAR_CODE.get(code)) || null;
}

/**
 * Ce qui part dans `personnes.ville` : le libellé français, jamais le code.
 * `VILLE_AUTRE` rend le texte libre saisi par le candidat, ramené à 80
 * caractères ; un code inconnu ne donne rien plutôt qu'une ville inventée.
 */
export function villeEnClair(code: string | null | undefined, texteLibre?: string | null): string | null {
  if (code === VILLE_AUTRE) return texteLibre?.trim().slice(0, 80) || null;
  return villeParCode(code)?.libelle ?? null;
}

/** Libellé affiché sur la page publique, dans la langue en cours. */
export function libelleVille(ville: Ville, langue: 'fr' | 'ar'): string {
  return langue === 'ar' ? ville.libelleAr : ville.libelle;
}

export interface GroupeVilles {
  libelle: string;
  villes: readonly Ville[];
}

/** La liste déroulante, groupée par gouvernorat, dans l'ordre de l'annexe B. */
export function villesGroupees(langue: 'fr' | 'ar'): GroupeVilles[] {
  const groupes: GroupeVilles[] = [];
  for (const ville of VILLES) {
    const libelle = langue === 'ar' ? ville.gouvernoratAr : ville.gouvernorat;
    const dernier = groupes[groupes.length - 1];
    if (dernier?.libelle === libelle) (dernier.villes as Ville[]).push(ville);
    else groupes.push({ libelle, villes: [ville] });
  }
  return groupes;
}
